import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Pagination,
} from "@mui/material";
import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import DesBtn from "../../Components/Reusable/DesBtn";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { popupState, authState } from "../../Recoil/RecoilState";
import VideosFilter from "../../Components/Shoplifting/VideosFilter";

const IncidentVideos = () => {
  const [allEvents, setAllEvents] = useState([]);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const itemsPerPage = 9;

  const setPopup = useSetRecoilState(popupState);
  const { token } = useRecoilValue(authState);

  const childRef = useRef(null);

  useEffect(() => {
    const fetchIncidents = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data } = await axios.get(
          `${baseURL}shoplifting/events`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const incidents = Array.isArray(data)
          ? data
          : data.items || data.data || [];

        setAllEvents(incidents);
        setEvents(incidents);
        setPage(1);
      } catch (err) {
        console.error("Error fetching incident videos:", err);

        setError(
          err.response?.data?.message ||
            "Failed to load incident videos. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchIncidents();
    }
  }, [token]);

  const handleActionChange = async (id, newAction) => {
    const oldEvent = allEvents.find(
      (ev) => ev.event_id === id
    );

    const updateFn = (list) =>
      list.map((ev) =>
        ev.event_id === id
          ? { ...ev, action_taken: newAction }
          : ev
      );

    setEvents((prev) => updateFn(prev));
    setAllEvents((prev) => updateFn(prev));

    try {
      await axios.post(
        `${baseURL}shoplifting/events/${id}/resolve`,
        {
          status: "confirmed",
          action_taken: newAction,
          description: "sdsad",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (err) {
      console.error(
        "Error updating event action:",
        err
      );

      const rollbackFn = (list) =>
        list.map((ev) =>
          ev.event_id === id
            ? {
                ...ev,
                action_taken:
                  oldEvent?.action_taken || "",
              }
            : ev
        );

      setEvents((prev) => rollbackFn(prev));
      setAllEvents((prev) => rollbackFn(prev));
    }
  };

  const applyFilter = (filterData) => {
    let filtered = [...allEvents];

    if (!filterData) {
      setEvents(filtered);
      setPage(1);
      return;
    }

    const getFilterArray = (val) => {
      if (Array.isArray(val)) return val.filter(Boolean);
      if (typeof val === "string" && val.trim()) return val.split(",").map((s) => s.trim()).filter(Boolean);
      return [];
    };

    const filterLocations = getFilterArray(filterData.locations);
    const filterAreas = getFilterArray(filterData.areas);
    const filterBuildings = getFilterArray(filterData.buildings);
    const filterFloors = getFilterArray(filterData.floors);
    const filterZones = getFilterArray(filterData.zones);

    if (filterLocations.length > 0) {
      filtered = filtered.filter((ev) =>
        filterLocations.includes(ev.camera_location || ev.location || ev.location_city)
      );
    }

    if (filterAreas.length > 0) {
      filtered = filtered.filter((ev) =>
        filterAreas.includes(ev.camera_area || ev.area)
      );
    }

    if (filterBuildings.length > 0) {
      filtered = filtered.filter((ev) =>
        filterBuildings.includes(ev.camera_building || ev.building)
      );
    }

    if (filterFloors.length > 0) {
      filtered = filtered.filter((ev) =>
        filterFloors.includes(ev.camera_floor || ev.floor_level || ev.floor)
      );
    }

    if (filterZones.length > 0) {
      filtered = filtered.filter((ev) =>
        filterZones.includes(ev.camera_zone || ev.zone)
      );
    }

    if (filterData.start_date) {
      const start = new Date(filterData.start_date);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter((ev) => {
        const evDate = new Date(ev.event_timestamp || ev.timestamp || ev.date);
        return !isNaN(evDate) && evDate >= start;
      });
    }

    if (filterData.end_date) {
      const end = new Date(filterData.end_date);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter((ev) => {
        const evDate = new Date(ev.event_timestamp || ev.timestamp || ev.date);
        return !isNaN(evDate) && evDate <= end;
      });
    }

    setEvents(filtered);
    setPage(1);
  };

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Filters",
      content: (
        <VideosFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={(newFilter) => {
            setFilter(newFilter);
          }}
        />
      ),
      sendReq: () => {
        childRef.current?.submit();
      },
    });
  };

  useEffect(() => {
    applyFilter(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, allEvents]);

  const totalPages = Math.ceil(
    events.length / itemsPerPage
  );

  const paginatedEvents = events.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 10,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          mb: 4,
          alignItems: "center",
        }}
      >
        <DesBtn
          text="Filter"
          handle={openFilter}
        >
          <FilterAltOutlinedIcon />
        </DesBtn>
      </Box>

      <Grid container spacing={3}>
        {paginatedEvents.map((ev) => (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            key={ev.event_id}
          >
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition:
                  "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            >
              <Box
                sx={{ position: "relative" }}
              >
                <video
                  controls
                  autoPlay={false}
                  width="100%"
                  style={{
                    borderRadius: "8px",
                    marginTop: "1rem",
                  }}
                >
                  <source
                    src={ev.video_path}
                    type="video/mp4"
                  />
                  Your browser does not support
                  the video tag.
                </video>

                <Chip
                  label={ev.severity}
                  color={
                    ev.severity === "high"
                      ? "error"
                      : ev.severity === "medium"
                      ? "warning"
                      : "success"
                  }
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    fontWeight: "bold",
                  }}
                />
              </Box>

              <CardContent
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6">
                    {ev.camera_name}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {new Date(
                      ev.event_timestamp
                    ).toLocaleString()}
                  </Typography>
                </Box>

                <Typography variant="body2">
                  <strong>Zone:</strong>{" "}
                  {ev.zone} |{" "}
                  <strong>Location:</strong>{" "}
                  {ev.camera_location}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <FormControl
                  size="small"
                  fullWidth
                  sx={{ mt: "auto" }}
                >
                  <InputLabel>
                    Action Taken
                  </InputLabel>

                  <Select
                    value={
                      ev.action_taken || ""
                    }
                    label="Action Taken"
                    onChange={(e) =>
                      handleActionChange(
                        ev.event_id,
                        e.target.value
                      )
                    }
                  >
                    <MenuItem value="intervened">
                      Intervened
                    </MenuItem>

                    <MenuItem value="police_called">
                      Police Called
                    </MenuItem>

                    <MenuItem value="">
                      No Action
                    </MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {events.length === 0 && (
          <Grid item xs={12}>
            <Typography
              variant="h6"
              color="text.secondary"
              align="center"
              sx={{ py: 5 }}
            >
              No incidents found matching
              criteria.
            </Typography>
          </Grid>
        )}
      </Grid>

      {events.length > itemsPerPage && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            color="primary"
            size="large"
            onChange={(_, value) => {
              setPage(value);

              window.scrollTo({
                top: 0,
                behavior: "smooth",
              });
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default IncidentVideos;